import { InputBase } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useSendUserFeedback } from "#app/monitoring/useSendUserFeedback";
import { defaultFiltersParams } from "#app/routes/pages/Search.page";
import { useLogout } from "#modules/auth/infra/useLogout";
import { useCompany } from "#modules/company/view/useCompany";
import { useMyModel } from "#modules/models/view/useModel";
import { useSearchByTerm } from "#modules/search/infra/useSearchByTerm";
import { Icon } from "#shared/icons/Icon";
import { ROUTES } from "#shared/routes";
import { useAnalytics } from "#shared/useAnalytics";
import { filtersParamsAtom, quoteRequestisEditing } from "#shared/view/components/AppShell/Atoms";
import { Avatar } from "#shared/view/components/AppShell/Avatar";
import { Brand } from "#shared/view/components/AppShell/Brand";
import { QuoteRequestInfo } from "#shared/view/components/AppShell/QuoteRequestInfo";
import { Flex } from "#shared/view/components/Flex";
import { Button } from "#shared/view/designSystem/Button";

import { ActionIcon } from "../ActionIcon";
import { Menu } from "../Menu";
import classes from "./Header.module.css";

export const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEditingQuoteRequest = useAtomValue(quoteRequestisEditing);
  const { logout } = useLogout();

  const goToSettings = () => {
    navigate(ROUTES.partnerSettings);
  };

  const { pathname } = useLocation();

  const { search, searchTermRef } = useSearchByTerm();
  const setFiltersParams = useSetAtom(filtersParamsAtom);

  const MANUAL_SEARCH_DEFAULT_COLUMNS = 8;
  const MANUAL_SEARCH_DEFAULT_LIMIT = 50;

  return (
    <Flex className={classes.header} gap="sm">
      <Brand className={classes.logo} align="center" />

      {isEditingQuoteRequest && <QuoteRequestInfo />}

      {!isEditingQuoteRequest && (
        <InputBase
          miw={350}
          ref={searchTermRef}
          placeholder={t("header.searchbar.placeholder")}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              search(searchTermRef.current?.value);
            }
          }}
          leftSection={<Icon name="search" />}
          onClick={() => {
            if (pathname !== ROUTES.partnerSearch) {
              setFiltersParams(defaultFiltersParams);
              navigate(
                `${ROUTES.partnerSearch}?columns=${MANUAL_SEARCH_DEFAULT_COLUMNS}&limit=${MANUAL_SEARCH_DEFAULT_LIMIT}`,
              );
            }
          }}
        />
      )}

      <Flex align={"center"} gap={"xs"}>
        <BugReportButton />
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon size="xl" radius="xl">
              <Avatar />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item leftSection={<Icon name="settings" />} onClick={goToSettings}>
              {t("header.settings")}
            </Menu.Item>
            <Menu.Item leftSection={<Icon name="logout" />} onClick={logout}>
              {t("header.logout")}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Flex>
    </Flex>
  );
};

const BugReportButton = () => {
  const { t } = useTranslation();
  const posthog = useAnalytics();
  const { company } = useCompany();
  const { model } = useMyModel();
  const { sendUserFeedbacks } = useSendUserFeedback();

  const handleClick = useCallback(async () => {
    const feedback = Sentry.getFeedback();
    if (!feedback) {
      return;
    }

    Sentry.setTags({
      "model.name": model?.name,
      "model.key": model?.key,
      "model.id": model?.id,
      "company.id": company?.id,
      "company.name": company?.name,
    });

    const form = await feedback.createForm({
      onSubmitSuccess: (data) => {
        sendUserFeedbacks({
          message: data.message,
          email: data.email,
          companyId: company?.id ?? "",
          companyName: company?.name ?? "",
          modelName: model?.name ?? "",
          modelId: model?.id ?? "",
          modelKey: model?.key ?? "",
          url: window.location.href,
          posthogSessionUrl: posthog?.__loaded
            ? posthog.get_session_replay_url({ withTimestamp: true, timestampLookBack: 30 })
            : undefined,
        });
      },
    });

    form.appendToDom();
    form.open();
  }, [sendUserFeedbacks, company, model, posthog]);

  return (
    <Button leftSection={<Icon name="chat" />} onClick={handleClick} variant="outline">
      {t("header.reportBugs")}
    </Button>
  );
};
